import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import {
  getAdminDashboardSnapshot,
  getRecentAdminActivity,
  type AdminActivityRow,
  type AdminDashboardSnapshot,
} from "../../../lib/admin";
import { COLORS, RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import {
  AdminError,
  AdminLoading,
  AdminPageHeader,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";
import { SectionHeader } from "../../../components/aspects/SectionHeader";

const THEME = PANE_THEMES.Admin;
const TZ = "America/New_York";

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: TZ,
  });
}

type StatTileSpec =
  | { kind: "delta"; label: string; value: number; delta: number; href?: Href }
  | { kind: "queue"; label: string; value: number; queueLabel: string; tone: "amber" | "red"; href: Href };

export default function AdminDashboardScreen() {
  const [snap, setSnap] = useState<AdminDashboardSnapshot | null>(null);
  const [activity, setActivity] = useState<AdminActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [s, a] = await Promise.all([getAdminDashboardSnapshot(), getRecentAdminActivity(12)]);
      setSnap(s);
      setActivity(a);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load dashboard");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const tiles: StatTileSpec[] = snap
    ? [
        { kind: "delta", label: "Students", value: snap.total_students, delta: snap.new_students_7d, href: "/admin/students" as Href },
        { kind: "delta", label: "Active judges", value: snap.total_active_judges, delta: snap.new_active_judges_7d, href: "/admin/users?role=judge" as Href },
        { kind: "delta", label: "Open competitions", value: snap.total_open_competitions, delta: snap.new_open_competitions_7d, href: "/admin/competitions" as Href },
        { kind: "queue", label: "Inquiries new", value: snap.inquiries_new, queueLabel: "waiting for reply", tone: snap.inquiries_new > 0 ? "red" : "amber", href: "/admin/inquiries" as Href },
        { kind: "queue", label: "Pending team", value: snap.pending_team, queueLabel: "waiting for approval", tone: "amber", href: "/admin/team" as Href },
        { kind: "queue", label: "Discovery calls", value: snap.discovery_calls_this_week, queueLabel: "this week", tone: "amber", href: "/admin/calendar" as Href },
      ]
    : [];

  const attentionRows = snap
    ? [
        snap.inquiries_new > 0
          ? { count: snap.inquiries_new, text: "inquir" + (snap.inquiries_new === 1 ? "y waiting for reply" : "ies waiting for reply"), href: "/admin/inquiries" as Href }
          : null,
        snap.pending_team > 0
          ? { count: snap.pending_team, text: "pending team application" + (snap.pending_team === 1 ? "" : "s"), href: "/admin/team" as Href }
          : null,
        snap.discovery_calls_this_week > 0
          ? { count: snap.discovery_calls_this_week, text: "discovery call" + (snap.discovery_calls_this_week === 1 ? "" : "s") + " this week", href: "/admin/calendar" as Href }
          : null,
        snap.drafts_stale_gt_7d > 0
          ? { count: snap.drafts_stale_gt_7d, text: "competition" + (snap.drafts_stale_gt_7d === 1 ? "" : "s") + " stuck in draft >7 days", href: "/admin/competitions" as Href }
          : null,
      ].filter((r): r is { count: number; text: string; href: Href } => r != null)
    : [];

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 40 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Admin · Overview"
          title="Platform overview"
          subtitle="Operations, users, competitions, and health."
        />

        {err ? (
          <View style={{ marginBottom: SPACING.md }}>
            <AdminError theme={THEME} message={err} onRetry={() => void load()} />
          </View>
        ) : null}

        {loading || !snap ? (
          <AdminLoading theme={THEME} label="Loading the platform snapshot…" />
        ) : (
          <>
            {/* Stat tiles */}
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
              {tiles.map((t) => (
                <MetricTile key={t.label} spec={t} />
              ))}
            </View>

            {/* Needs your attention */}
            <View style={{ marginTop: SPACING.xl }}>
              <SectionHeader theme={THEME} title="Needs your attention" tone="dark" count={attentionRows.length} />
              {attentionRows.length === 0 ? (
                <View
                  style={{
                    paddingVertical: 22,
                    alignItems: "center",
                    backgroundColor: THEME.surfaceBg,
                    borderRadius: RADII.md,
                    borderWidth: STROKES.hairline,
                    borderColor: THEME.border,
                  }}
                >
                  <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 18, color: THEME.textOnSurface }}>
                    Nothing&apos;s blocked.
                  </Text>
                  <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
                    Take a breath.
                  </Text>
                </View>
              ) : (
                <ListRowGroup theme={THEME}>
                  {attentionRows.map((r, idx) => (
                    <ListRow
                      key={idx}
                      theme={THEME}
                      first={idx === 0}
                      onPress={() => router.push(r.href)}
                      leading={
                        <Text
                          style={{
                            fontFamily: THEME.displayMediumFont,
                            fontSize: 22,
                            color: THEME.accent,
                            minWidth: 32,
                            textAlign: "right",
                          }}
                        >
                          {r.count}
                        </Text>
                      }
                      title={r.text}
                      trailing={
                        <Text
                          style={{
                            fontFamily: THEME.bodyBoldFont,
                            fontSize: 10.5,
                            letterSpacing: 1.4,
                            textTransform: "uppercase",
                            color: THEME.accent,
                          }}
                        >
                          Open →
                        </Text>
                      }
                    />
                  ))}
                </ListRowGroup>
              )}
            </View>

            {/* Recent activity */}
            <View style={{ marginTop: SPACING.xl }}>
              <SectionHeader theme={THEME} title="Recent activity" tone="dark" />
              {activity.length === 0 ? (
                <View
                  style={{
                    padding: 16,
                    backgroundColor: THEME.surfaceBg,
                    borderRadius: RADII.md,
                    borderWidth: STROKES.hairline,
                    borderColor: THEME.border,
                  }}
                >
                  <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
                    No activity yet.
                  </Text>
                </View>
              ) : (
                <ListRowGroup theme={THEME}>
                  {activity.map((row, idx) => (
                    <ListRow
                      key={row.id}
                      theme={THEME}
                      first={idx === 0}
                      onPress={row.href ? () => router.push(row.href as Href) : undefined}
                      leading={<ActivityDot kind={row.kind} />}
                      title={row.title}
                      subtitle={row.subtitle ?? undefined}
                      meta={relTime(row.ts)}
                    />
                  ))}
                </ListRowGroup>
              )}
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
}

function ActivityDot({ kind }: { kind: AdminActivityRow["kind"] }) {
  const color =
    kind === "inquiry"
      ? THEME.accent
      : kind === "team_application"
        ? THEME.destructive
        : kind === "competition_created"
          ? COLORS.SAGE.hex
          : THEME.textOnSurfaceDim;
  return (
    <View
      style={{
        width: 6,
        height: 6,
        borderRadius: RADII.full,
        backgroundColor: color,
      }}
    />
  );
}

function MetricTile({ spec }: { spec: StatTileSpec }) {
  const alert = spec.kind === "queue" && spec.tone === "red" && spec.value > 0;
  const captionColor =
    spec.kind === "delta"
      ? spec.delta > 0
        ? THEME.accent
        : THEME.textOnSurfaceFaint
      : spec.value > 0
        ? alert
          ? THEME.destructive
          : THEME.accent
        : THEME.textOnSurfaceFaint;
  const caption =
    spec.kind === "delta"
      ? spec.delta > 0
        ? `↑ ${spec.delta} this week`
        : "no change this week"
      : spec.queueLabel;

  const href = spec.href;
  return (
    <Pressable
      onPress={href ? () => router.push(href) : undefined}
      disabled={!href}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          flexBasis: "30%",
          flexGrow: 1,
          minWidth: 180,
          padding: 16,
          borderRadius: RADII.md,
          backgroundColor: THEME.surfaceRaisedBg,
          borderWidth: STROKES.hairline,
          borderColor: alert
            ? THEME.destructive
            : hovered && href
              ? THEME.accentBorderHover
              : THEME.border,
        },
        Platform.OS === "web"
          ? ({
              cursor: href ? "pointer" : "default",
              transition: "border-color 0.16s ease, transform 0.16s ease",
              transform: hovered && href ? "translateY(-1px)" : "translateY(0)",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: THEME.bodyBoldFont,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: THEME.textOnSurfaceDim,
        }}
      >
        {spec.label}
      </Text>
      <Text style={{ marginTop: 6, fontFamily: THEME.displayMediumFont, fontSize: 28, color: THEME.textOnSurface }}>
        {spec.value.toLocaleString()}
      </Text>
      <Text style={{ marginTop: 4, fontFamily: THEME.bodyMediumFont, fontSize: 11, color: captionColor }}>
        {caption}
      </Text>
    </Pressable>
  );
}
